<template>
  <div
    class="step-card text-gray03"
    @keydown.alt.50="nextPage()"
    @keydown.alt.49="previousPage()"
  >
    <div class="card card-survey-steps">
      <div class="card-answer-wrapper card-answer-ie-11">
        <Answer
          v-if="currentQuestion && currentQuestion.type !== 'feedback'"
          :question="currentQuestion"
          :page="surveyPage"
          :key="currentQuestion.question_id"
          @answer="selectAnswer"
        />

        <Feedback
          v-if="currentQuestion && currentQuestion.type === 'feedback'"
          :question="currentQuestion"
          @selectFeedback="selectFeedback"
        />
      </div>

      <div class="card-footer text-muted">
        <b-button
          id="btn-previous"
          @click="previousPage()"
          :aria-label="`${resources.component.steps.previous}, Alt + 1`"
          :class="{
            'mdi mdi-loading mdi-spin': surveyPage.loading,
            'btn-previous': surveyPage.current === 1,
          }"
          variant="light"
          :tabindex="currentQuestionAnswersCount + 4"
          :disabled="surveyPage.loading || surveyPage.current === 1"
          :aria-hidden="surveyPage.loading || surveyPage.current === 1"
        >
          <i class="mdi mdi-arrow-left"></i>
          <span>{{ resources.component.steps.previous }}</span>
        </b-button>

        <div class="flex-grow" aria-hidden="true">
          <PageIndicators class="page-indicator" :page="surveyPage" />
        </div>

        <b-button
          id="btn-next"
          @click="nextPage()"
          :aria-label="`${labelButtonNext}, Alt + 2`"
          :tabindex="currentQuestionAnswersCount + 5"
          :class="{ 'mdi mdi-loading mdi-spin': surveyPage.loading }"
          variant="light"
          :disabled="surveyPage.loading"
        >
          <span>{{ labelButtonNext }}</span>
          <i v-if="hasNextPage" class="mdi mdi-arrow-right"></i>
          <i v-else class="mdi mdi-checkbox-marked-circle-outline"></i>
        </b-button>
      </div>
    </div>

    <b-modal
      id="modal-error"
      :title="resources.page.survey.errorMessage.title"
      cancel-variant="outline-primary"
      ref="modal-error"
      header-bg-variant="danger"
    >
      <p class="txt-20">
        {{ resources.page.survey.errorMessage.message1 }}
      </p>
      <p class="txt-20">
        {{ resources.page.survey.errorMessage.message2 }}
      </p>
      <template v-slot:modal-footer>
        &nbsp;
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Answer from '@/components/CustomSurveys/Survey/Questions/Answer.vue'
import PageIndicators from '@/components/CustomSurveys/Survey/PageIndicators.vue'
import Feedback from '@/components/CustomSurveys/Survey/Questions/Feedback.vue'

export default {
  components: {
    Answer,
    PageIndicators,
    Feedback,
  },

  data() {
    return {
      hasUnsavedAnswer: false,
    }
  },

  computed: {
    ...mapGetters({
      currentQuestion: 'customSurvey/currentQuestion',
      surveyPage: 'customSurvey/surveyPage',
      resources: 'resources',
    }),

    labelButtonNext: function() {
      if (!this.hasNextPage) {
        return this.resources.page.customSurveys.survey.finished
      }

      return this.currentQuestion && this.currentQuestion.answered
        ? this.resources.component.steps.next
        : this.resources.component.steps.skip
    },

    currentQuestionAnswersCount() {
      return this.currentQuestion && this.currentQuestion.answers
        ? this.currentQuestion.answers.length
        : 0
    },

    hasNextPage() {
      return this.surveyPage.current < this.surveyPage.total
    },
  },

  methods: {
    async saveSurvey() {
      if (!this.hasUnsavedAnswer) {
        return true
      }

      this.hasUnsavedAnswer = false
      try {
        this.$store.commit('customSurvey/toggleSurveyPostLoading')
        await this.$store.dispatch('customSurvey/saveSurvey')
        this.$store.commit('customSurvey/toggleSurveyPostLoading')
        return true
      } catch (e) {
        this.hasUnsavedAnswer = true
        this.$store.commit('customSurvey/toggleSurveyPostLoading')
        this.$refs['modal-error'].show()
        return false
      }
    },

    async nextPage() {
      if (!this.hasNextPage) {
        return this.finishSurvey()
      }

      const goNextPage = await this.saveSurvey()
      if (goNextPage) {
        this.$store.dispatch('customSurvey/surveyNextPage')
      }
    },

    selectAnswer(answer) {
      this.hasUnsavedAnswer = true
      this.$store.commit('customSurvey/selectAnswer', answer)
    },

    selectFeedback(feedback) {
      this.hasUnsavedAnswer = true
      this.$store.commit('customSurvey/selectAnswerFeedback', feedback)
    },

    async previousPage() {
      if (this.surveyPage.current === 1) return

      const goPreviousPage = await this.saveSurvey()
      if (goPreviousPage) {
        this.$store.dispatch('customSurvey/surveyPreviousPage')
      }
    },

    async finishSurvey() {
      try {
        this.$store.commit('customSurvey/toggleSurveyPostLoading')
        await this.$store.dispatch('customSurvey/finishSurvey')
        this.$router.push({ name: 'custom-surveys.survey.conclusion' })
      } catch (e) {
        this.$store.commit('customSurvey/toggleSurveyPostLoading')
        this.$refs['modal-error'].show()
      }
    },
  },
}
</script>

<style scoped lang="scss">
@import '~@/styles/custom/colors';

_:-ms-fullscreen,
:root .card-answer-ie-11 {
  display: block !important;
}

.step-card {
  background-image: linear-gradient(
    to bottom,
    rgba(0, 0, 255, 0) 20%,
    map-get($theme-colors, 'light') 20%
  );
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  min-height: 100%;
  overflow: auto;
  padding: 6rem 0;
  position: absolute;
  right: 0;
  top: 0;

  @media (max-height: 800px) {
    padding: 2rem 0;
  }

  @media (max-width: 425px) {
    padding: 0;
  }

  .card-survey-steps {
    display: flex;
    flex-direction: column;
    max-width: 80%;
    text-align: center;
    width: 100%;

    @media (max-width: 768px) {
      max-width: 95%;
    }

    @media (max-width: 425px) {
      border-radius: 0;
      max-width: initial;
    }

    .card-answer-wrapper {
      display: flex;
      flex-grow: 1;
      justify-content: center;
      overflow: auto;
      padding: 3rem 1rem;

      @media (max-width: 768px) {
        padding: 1.5rem;
        text-align: center;
      }

      @media (max-width: 425px) {
        padding: 1rem;
      }

      @media (max-height: 520px) {
        padding: 0.1rem 1rem;
      }
    }

    .card-footer {
      align-items: center;
      background-color: map-get($theme-colors, 'gray06');
      display: flex;
      min-height: 70px;
      width: 100%;

      @media (max-width: 768px) {
        border-radius: 0;
      }

      @media (max-width: 425px) {
        min-height: 70px;
      }

      .flex-grow {
        flex-grow: 1;
      }

      .btn {
        align-items: center;
        background: none;
        border: 1px solid map-get($theme-colors, 'gray-dark');
        color: map-get($theme-colors, 'gray03');
        display: flex;
        justify-content: space-between;
        padding: 0px 15px;
        width: 230px;

        &:not(:disabled):active {
          background-color: map-get($theme-colors, 'red');
          border: 1px solid map-get($theme-colors, 'red');
          color: map-get($theme-colors, 'white');
        }

        &.btn-previous:disabled {
          opacity: 0;
        }

        @media (max-width: 900px) {
          font-size: 22px;
          justify-content: center;
          padding: 5px 16px;
          width: 50px;
          height: 50px !important;
        }

        @media (max-width: 320px) {
          font-size: 15px;
          width: 18px;
          height: 34px !important;
        }

        span {
          font-size: 1rem;

          @media (max-width: 900px) {
            display: none;
          }
        }
      }
    }
  }
}
</style>
